import { DBFactory } from "~/classes/database/db_factory";
import { NextAssignmentVisibilities } from "~/types/enums/NextAssignmentVisibilities.enum";
import { BaseModel } from "../base.model";
import {
  EducationResource,
  EducationResources,
} from "../education-resources/education-resource.model";

import { useWordGoogleFormats } from "~/composables/useWordGoogleFormats";

export type Classrooms = Classroom[];

export type WritingCompanionPurchasePolicy =
  | "off"
  | "optional"
  | "required";

export type WritingCompanionPolicy =
  | "off"
  | "student_optional"
  | "student_required"
  | "teacher_sponsored"
  | "organization_sponsored";

export const normalizeWritingCompanionPolicy = (
  policy?: WritingCompanionPolicy | WritingCompanionPurchasePolicy
): WritingCompanionPolicy => {
  if (policy === "optional") return "student_optional";
  if (policy === "required") return "student_required";
  return policy ?? "off";
};

export type ClassroomConstructorParams = {
  id?: string;
  externalId: string;
  organizationId?: string;
  name: string;
  section?: string;
  label: string;
  externalProvider: "sakai" | "google" | "canvas" | "iew";
  externalProviderUserId: string;
  userId: string;
  shouldHide?: boolean;
  lastSyncedTimestamp?: number;
  isArchived: boolean;
  classroomAssignmentGroups?: ClassroomAssignmentGroups;
  numGradedDocuments?: number;
  enableStudentReview?: boolean;
  enableStudentSubmissionCreation?: boolean;
  studentSubmissionCreationStudentIds?: string[];
  writingCompanionPurchasePolicy?: WritingCompanionPurchasePolicy;
  writingCompanionPolicy?: WritingCompanionPolicy;
  enableStudentCompare?: boolean;
  studentCompareStudentIds?: string[];
  enableStudentThesaurus?: boolean;
  studentThesaurusStudentIds?: string[];
  enableClassSessions?: boolean;
  allowKWOSubmission: boolean;
  requireKWOSubmission?: boolean;
  allowRoughDraftSubmission: boolean;
  requireRoughDraftSubmission?: boolean;
  displayOrder?: number;
  resources?: EducationResources;
  requireRoughDraftChecklist?: boolean;
  roughDraftSubmissionFormats?: string[];
  isAutoReturnEnabled: boolean;
  nextAssignmentVisibility?: NextAssignmentVisibilities;
  enableSubmissionBadges?: boolean;

  enableStudentStagingRedirect?: boolean;

  // If set to true, teacher will be probmpet to mark the final draft as  accept / incomplete instead of showing a grade to the student
  allowFinalDraftGradedState?: boolean;
};
export class Classroom extends BaseModel {
  id?: string;
  externalId: string;
  organizationId?: string;
  name: string = "";
  label: string = "";
  section?: string = "";
  externalProvider: "sakai" | "google" | "canvas" | "iew" = "iew";
  externalProviderUserId: string = "";
  userId: string = "";
  shouldHide?: boolean = false;
  lastSyncedTimestamp?: number;
  isArchived: boolean = false;
  classroomAssignmentGroups?: ClassroomAssignmentGroups;
  numGradedDocuments?: number;
  enableStudentReview: boolean;
  enableStudentSubmissionCreation: boolean;
  studentSubmissionCreationStudentIds: string[];
  writingCompanionPurchasePolicy?: WritingCompanionPurchasePolicy;
  writingCompanionPolicy: WritingCompanionPolicy;
  enableStudentCompare: boolean;
  studentCompareStudentIds: string[];
  enableStudentThesaurus: boolean;
  studentThesaurusStudentIds: string[];
  enableClassSessions: boolean;
  allowKWOSubmission?: boolean;
  requireKWOSubmission?: boolean;
  allowRoughDraftSubmission?: boolean;
  requireRoughDraftSubmission?: boolean;
  allowFinalDraftGradedState?: boolean;
  displayOrder?: number;
  resources: EducationResources;
  requireRoughDraftChecklist: boolean;
  roughDraftSubmissionFormats?: string[];
  isAutoReturnEnabled: boolean;
  nextAssignmentVisibility: NextAssignmentVisibilities;
  enableSubmissionBadges: boolean;
  // If true, students will see a message and then be redirected to the staging page
  enableStudentStagingRedirect: boolean;
  constructor(data: ClassroomConstructorParams) {
    super();

    this.id = data.id;
    this.externalId = data.externalId;
    this.organizationId = data.organizationId;
    this.name = data.name;
    this.label = data.label;
    this.section = data.section;
    this.externalProvider = data.externalProvider;
    this.externalProviderUserId = data.externalProviderUserId;
    this.userId = data.userId;
    this.shouldHide = data.shouldHide;
    this.lastSyncedTimestamp = data.lastSyncedTimestamp ?? 0;
    this.isArchived = data.isArchived ?? false;
    this.classroomAssignmentGroups = data.classroomAssignmentGroups ?? [];
    this.numGradedDocuments = data.numGradedDocuments;

    this.enableStudentReview = data.enableStudentReview ?? false;
    this.enableStudentSubmissionCreation =
      data.enableStudentSubmissionCreation ?? false;
    this.studentSubmissionCreationStudentIds =
      data.studentSubmissionCreationStudentIds ?? [];
    this.writingCompanionPurchasePolicy =
      data.writingCompanionPurchasePolicy;
    this.writingCompanionPolicy = normalizeWritingCompanionPolicy(
      data.writingCompanionPolicy ?? data.writingCompanionPurchasePolicy
    );

    this.enableStudentCompare = data.enableStudentCompare ?? false;
    this.studentCompareStudentIds = data.studentCompareStudentIds ?? [];

    this.enableStudentThesaurus = data.enableStudentThesaurus ?? false;
    this.studentThesaurusStudentIds = data.studentThesaurusStudentIds ?? [];

    this.enableClassSessions = data.enableClassSessions ?? false;

    this.allowKWOSubmission = data.allowKWOSubmission ?? false;
    this.requireKWOSubmission =
      data.requireKWOSubmission ?? (data.allowKWOSubmission ? true : false);
    this.allowRoughDraftSubmission = data.allowRoughDraftSubmission ?? false;
    this.requireRoughDraftSubmission =
      data.requireRoughDraftSubmission ??
      (data.allowRoughDraftSubmission ? true : false);

    this.allowFinalDraftGradedState = data.allowFinalDraftGradedState ?? false;

    this.resources = data.resources ?? [];

    this.displayOrder = data.displayOrder ?? 0;

    this.requireRoughDraftChecklist = data.requireRoughDraftChecklist ?? true;
    this.roughDraftSubmissionFormats =
      data.roughDraftSubmissionFormats ?? useWordGoogleFormats();
    this.isAutoReturnEnabled = data.isAutoReturnEnabled ?? true;
    this.nextAssignmentVisibility =
      data.nextAssignmentVisibility ?? NextAssignmentVisibilities.submitted;

    this.enableSubmissionBadges =  data.enableSubmissionBadges ?? true;
    this.enableStudentStagingRedirect =
      data.enableStudentStagingRedirect ?? false;

    this.modelDatabaseConfig = {
      collection: "/classrooms",
      path: `/classrooms/${this.id}`,
    };
  }

  override get databaseConfig() {
    return {
      path: `classrooms/${this.id}`,
      collection: "classrooms",
    };
  }

  // Override toMap if you have special logic for converting to a map
  override toMap(): ModelDatabaseData {
    const map = super.toMap();
    delete map.writingCompanionPurchasePolicy;
    return map;
  }

  static default(
    userId: string | undefined,
    options?: {
      organizationId?: string;
    }
  ) {
    return new Classroom({
      name: "",
      label: "",
      externalId: "",
      organizationId: options?.organizationId,
      externalProvider: "iew",
      externalProviderUserId: userId ?? "",
      userId: userId ?? "",
      shouldHide: false,
      isArchived: false,
      classroomAssignmentGroups: [],
      enableStudentReview: false,
      writingCompanionPolicy: "off",
      enableClassSessions: false,
      allowKWOSubmission: false,
      requireKWOSubmission: false,
      allowRoughDraftSubmission: false,
      requireRoughDraftSubmission: false,
      resources: [],
      isAutoReturnEnabled: true,
      nextAssignmentVisibility: NextAssignmentVisibilities.submitted,
    });
  }

  // Override fromMap if you have special logic for creating an instance from a map
  static override fromMap(map: any): Classroom {
    if (map["resources"]) {
      map["resources"] = map["resources"].map((resource: any) =>
        EducationResource.fromMap(resource)
      );
    }

    return new Classroom(map);
  }

  async save() {
    const db = DBFactory.createDatabase();
    this.id = await db.save(this.toMap(), this.databaseConfig);
  }
}
